'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { LinkData } from '@eliadmin/core/src/utils/svetech/text';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import {
  CaptchaAction,
  useRecaptcha,
} from '@eliadmin/core/src/components/providers/captcha-provider';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Button, ButtonLink } from '@eliadmin/core/src/components/svetech/ui/button';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { RichText } from '@eliadmin/core/src/components/svetech/ui/rich-text';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type FormFieldText = { label: string; placeholder: string };

export type ContactFormTexts = {
  title: string;
  note: string;
  submit: string;
  /** Consent sentence (HTML with the link to the privacy page). */
  consent: string;
  fields: Record<'name' | 'email' | 'phone' | 'text', FormFieldText>;
};

/** Domov fields `Kontaktný blok - odoslané …`. */
export type ContactSentTexts = {
  title: string;
  /** HTML */
  text: string;
  /** `<li>` contents (HTML). */
  steps: string[];
  primary: LinkData | null;
  secondary: LinkData | null;
};

type Field = 'name' | 'email' | 'phone' | 'text' | 'gdpr';
type Errors = Partial<Record<Field, string>>;

const EMPTY = { name: '', email: '', phone: '', text: '', gdpr: false };
const FIELD_ORDER: Field[] = ['name', 'email', 'phone', 'text', 'gdpr'];

/** Figma „Field“: 56 px, radius 12/0/12/12, padding 16/12 on phones (4701:4439); 50 px, 15/0/15/15, 16 on desktop (4477:1446). */
const fieldClassName =
  'svt-body w-full rounded-xl rounded-tr-none border border-[#e6e8e2] bg-white px-3 text-[#14171a] placeholder:text-[#6b7681] focus:border-[#2c7a25] focus:outline-none aria-[invalid=true]:border-[#b3261e] md:rounded-[15px] md:rounded-tr-none md:px-4';

/**
 * Inquiry form of the contact block → `/store/forms/contact`. Labels come from the Domov page
 * (`Kontaktný blok - pole …`), validation and error messages are universal texts (`t()`). After a
 * successful submit the form gives way to the `sent` panel (Figma „Kontakt — formulár odoslaný“).
 */
export function ContactForm({
  title,
  note,
  submit,
  consent,
  fields,
  sent,
}: ContactFormTexts & { sent: ContactSentTexts }) {
  const { t } = useTranslations();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const form = useRef<HTMLFormElement>(null);
  const locale = useLocaleStore(state => state.currentLocale);
  const { executeRecaptcha } = useRecaptcha();

  const set = (field: Field, value: string | boolean) => {
    setValues(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: undefined }));
  };

  const validate = (): Errors => {
    const found: Errors = {};
    const required = t('text734');
    if (!values.name.trim()) found.name = required;
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) found.email = t('text732');
    if (!values.phone.trim()) found.phone = required;
    if (!values.text.trim()) found.text = required;
    if (!values.gdpr) found.gdpr = t('text740');
    return found;
  };

  const focusFirstError = (found: Errors) => {
    const first = FIELD_ORDER.find(field => found[field]);
    if (first) form.current?.querySelector<HTMLElement>(`[name="contact-${first}"]`)?.focus();
  };

  if (status === 'sent' && sent.title)
    return <SentPanel {...sent} onLeave={() => setStatus('idle')} />;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      focusFirstError(found);
      return;
    }

    setStatus('sending');
    try {
      const captcha = executeRecaptcha ? await executeRecaptcha(CaptchaAction.CONTACT) : undefined;
      await sendContactMessage({
        locale,
        data: {
          subject: `SVETECH WEB | DOPYT | ${values.name.trim()}`.slice(0, 150),
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          text: values.text.trim(),
          gdpr: true,
          'g-recaptcha-response': captcha,
        },
      });
      trackFormSubmitSuccess({ form_id: 'contact', form_name: 'contact_block' });
      setValues(EMPTY);
      setStatus('sent');
    } catch (error) {
      if (error instanceof ValidationError) {
        const serverErrors = Object.fromEntries(
          Object.entries(error.fields).map(([key, messages]) => [key, messages.join(' ')]),
        ) as Errors;
        setErrors(serverErrors);
        focusFirstError(serverErrors);
      }
      setStatus('failed');
    }
  };

  return (
    <form ref={form} noValidate onSubmit={onSubmit} className="flex flex-col gap-6 md:gap-8">
      {title ? <Heading tag="h3" title={title} /> : null}
      <div className="flex flex-col gap-4">
        <Input
          id="name"
          text={fields.name}
          autoComplete="name"
          value={values.name}
          error={errors.name}
          onChange={value => set('name', value)}
        />
        <div className="flex flex-col gap-4 md:flex-row">
          <Input
            id="email"
            type="email"
            text={fields.email}
            autoComplete="email"
            value={values.email}
            error={errors.email}
            onChange={value => set('email', value)}
          />
          <Input
            id="phone"
            type="tel"
            text={fields.phone}
            autoComplete="tel"
            value={values.phone}
            error={errors.phone}
            onChange={value => set('phone', value)}
          />
        </div>
        <Input
          id="text"
          text={fields.text}
          value={values.text}
          error={errors.text}
          onChange={value => set('text', value)}
          multiline
        />
        <div className="flex flex-col gap-2">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              name="contact-gdpr"
              checked={values.gdpr}
              required
              aria-invalid={Boolean(errors.gdpr)}
              aria-describedby={errors.gdpr ? 'contact-gdpr-error' : undefined}
              onChange={event => set('gdpr', event.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className="flex size-6 shrink-0 items-center justify-center rounded border border-[#e6e8e2] bg-white text-white peer-checked:border-[#2c7a25] peer-checked:bg-[#2c7a25] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2c7a25] peer-focus-visible:ring-offset-2 peer-aria-[invalid=true]:border-[#b3261e]"
            >
              <svg viewBox="0 0 12 10" className="w-3" fill="none">
                <path
                  d="M2 5L5 8L10 2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {/* Phrasing content only inside a label: the admin paragraph is unwrapped. */}
            <RichText
              as="span"
              html={consent.replace(/<\/?p\b[^>]*>/gi, '')}
              variant="svt-body"
              className="text-[#14171a] [&_a]:font-medium [&_a]:no-underline hover:[&_a]:underline"
            />
          </label>
          {errors.gdpr ? <ErrorText id="contact-gdpr-error">{errors.gdpr}</ErrorText> : null}
        </div>
      </div>
      {/* Phones: the full-width button above the note (4701:4462). */}
      <div className="flex flex-col-reverse gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1 md:max-w-[323px]" role="status">
          {status === 'sent' ? (
            <p className="svt-body-s font-semibold text-[#2c7a25]">{t('text428')}</p>
          ) : status === 'failed' ? (
            <p className="svt-body-s font-semibold text-[#b3261e]">{t('text429')}</p>
          ) : note ? (
            <p className="svt-body-s text-[#6b7681]">{note}</p>
          ) : null}
        </div>
        <Button
          type="submit"
          disabled={status === 'sending'}
          className="disabled:opacity-70 max-md:w-full"
        >
          {submit || t('text1118')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Figma „Odoslané“ (4735:5879, desktop): 64 px #fdf4ce tile with the green seal, H3 + Body L 8 px
 * apart, the next steps on a #f2f3ef panel (20/0/20/20, padding 24, rows 12 px apart: 20 px seal +
 * 14/20 #6b7681) and the two buttons, all 24 px apart. Phones (derived, no Figma frame): the 50 px
 * tile of the contact rows, padding 16 and full-width buttons like the CTA banner.
 */
/** `onLeave`: a link to the current page keeps the block mounted — show the form again. */
function SentPanel({
  title,
  text,
  steps,
  primary,
  secondary,
  onLeave,
}: ContactSentTexts & { onLeave: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  const headingId = 'contact-sent-title';

  // The form (and the focused submit button) is gone: move focus to the confirmation.
  useEffect(() => panel.current?.focus(), []);

  return (
    <div
      ref={panel}
      tabIndex={-1}
      role="status"
      aria-labelledby={headingId}
      onClick={event => {
        if ((event.target as HTMLElement).closest('a')) onLeave();
      }}
      className="flex scroll-mt-6 flex-col items-start gap-6 focus:outline-none"
    >
      <span className="flex size-[50px] items-center justify-center rounded-xl rounded-tr-none bg-[#fdf4ce] text-[#2c7a25] md:size-16 md:rounded-[15px] md:rounded-tr-none">
        <Icon name="seal-check" className="size-6 md:size-8" />
      </span>
      <div className="flex flex-col gap-2">
        <Heading tag="h3" id={headingId} title={title} />
        <RichText html={text} variant="svt-body-l" className="text-[#4a5560]" />
      </div>
      {steps.length ? (
        <ul className="flex w-full flex-col gap-3 rounded-[20px] rounded-tr-none bg-[#f2f3ef] p-4 md:p-6">
          {steps.map((step, index) => (
            <li key={index} className="flex items-start gap-3">
              <Icon name="seal-check" className="size-5 shrink-0 text-[#2c7a25]" />
              <RichText
                as="span"
                html={step}
                variant="svt-body-s"
                className="leading-5 text-[#6b7681]"
              />
            </li>
          ))}
        </ul>
      ) : null}
      {primary || secondary ? (
        <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4">
          {primary ? (
            <ButtonLink href={primary.href} className="max-md:w-full">
              {primary.label}
            </ButtonLink>
          ) : null}
          {secondary ? (
            <ButtonLink
              href={secondary.href}
              variant="text"
              icon="package"
              className="max-md:w-full"
            >
              {secondary.label}
            </ButtonLink>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type InputProps = {
  id: 'name' | 'email' | 'phone' | 'text';
  text: FormFieldText;
  value: string;
  error?: string;
  type?: 'text' | 'email' | 'tel';
  autoComplete?: string;
  multiline?: boolean;
  onChange: (value: string) => void;
};

function Input({ id, text, error, multiline, onChange, ...props }: InputProps) {
  const inputId = `contact-${id}`;
  const describedBy = error ? `${inputId}-error` : undefined;
  const shared = {
    id: inputId,
    name: inputId,
    placeholder: text.placeholder,
    value: props.value,
    required: true,
    'aria-invalid': Boolean(error),
    'aria-describedby': describedBy,
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <label htmlFor={inputId} className="svt-label text-[#14171a]">
        {text.label}
      </label>
      {multiline ? (
        <textarea
          {...shared}
          rows={3}
          onChange={event => onChange(event.target.value)}
          className={cn(fieldClassName, 'h-14 resize-y py-4 md:h-[100px]')}
        />
      ) : (
        <input
          {...shared}
          type={props.type ?? 'text'}
          autoComplete={props.autoComplete}
          onChange={event => onChange(event.target.value)}
          className={cn(fieldClassName, 'h-14 md:h-[50px]')}
        />
      )}
      {error ? <ErrorText id={`${inputId}-error`}>{error}</ErrorText> : null}
    </div>
  );
}

function ErrorText({ id, children }: { id: string; children: string }) {
  return (
    <p id={id} className="svt-body-s text-[#b3261e]">
      {children}
    </p>
  );
}
